import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import Stepper from "../components/Stepper";
import { AUTH_CONTEXT_TYPE } from "../../../utils/authPortal";
import { isAuth } from "../../../utils/Auth";
import {
  getClientPortalLogin,
  isClientMustChangePasswordLocked,
} from "../../../utils/clientPortal";
import {
  getCompanyResetPasswordLockRedirectState,
  getResolvedCompanyPortalSession,
  isCompanyMustChangePasswordLocked,
} from "../../../utils/companyRegistration";
import messages from "../../../assets/locale/messages";
import ResetPasswordStep1 from "./ResetPasswordStep1";
import ResetPasswordStep2 from "./ResetPasswordStep2";
import "./resetPassword.scss";

const TOTAL_STEPS = 2;

const formatStepLabel = (template, stepDisplay, totalDisplay) =>
  template.replace("{step}", stepDisplay).replace("{total}", totalDisplay);

/**
 * Company reset-password: step 1 = credentials (sessions), step 2 = new password (change_password).
 * Stepper shows 01/02 on both steps so users see the full path. `must_change_password` from login
 * lands on step 2 (step 1 shown completed in the stepper).
 */
const ResetPasswordFlow = ({
  type = AUTH_CONTEXT_TYPE.COMPANY,
}) => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const { resetPassword: t } = messages[lang];

  const sessionUser = useMemo(() => {
    if (type === AUTH_CONTEXT_TYPE.COMPANY) {
      return getResolvedCompanyPortalSession();
    }
    if (type === AUTH_CONTEXT_TYPE.CLIENT) {
      return getClientPortalLogin();
    }
    return login;
  }, [type, login]);

  const mustChangeLocked = useMemo(() => {
    if (!isAuth()) return false;
    if (type === AUTH_CONTEXT_TYPE.COMPANY) {
      return isCompanyMustChangePasswordLocked(sessionUser);
    }
    if (type === AUTH_CONTEXT_TYPE.CLIENT) {
      return isClientMustChangePasswordLocked(sessionUser);
    }
    return false;
  }, [type, sessionUser]);

  const sessionEmail = useMemo(() => {
    const email = sessionUser?.email;
    return typeof email === "string" ? email.trim() : "";
  }, [sessionUser]);

  useEffect(() => {
    if (!mustChangeLocked || location.state?.advanceToStep2) {
      return;
    }
    const redirectState =
      type === AUTH_CONTEXT_TYPE.COMPANY
        ? getCompanyResetPasswordLockRedirectState(sessionUser)
        : {
            advanceToStep2: true,
            email: sessionEmail,
            resetPasswordFormKey: Date.now(),
          };
    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: redirectState,
    });
  }, [
    mustChangeLocked,
    location.state?.advanceToStep2,
    location.pathname,
    location.search,
    history,
    type,
    sessionUser,
    sessionEmail,
  ]);

  const prefilledEmail = useMemo(() => {
    if (mustChangeLocked && sessionEmail) {
      return sessionEmail;
    }
    const fromState =
      location.state && typeof location.state.email === "string"
        ? location.state.email.trim()
        : "";
    if (fromState) return fromState;
    const params = new URLSearchParams(location.search);
    const q = params.get("email");
    return q ? String(q).trim() : "";
  }, [location.search, location.state, mustChangeLocked, sessionEmail]);

  const emailLocked = Boolean(prefilledEmail);

  const advanceToStep2 =
    Boolean(location.state?.advanceToStep2) || mustChangeLocked;
  const currentStep = advanceToStep2 ? 2 : 1;

  const step1Key =
    location.state && typeof location.state.resetPasswordFormKey === "number"
      ? location.state.resetPasswordFormKey
      : "reset-password-step-initial";

  const stepProgressLabel = useMemo(
    () =>
      formatStepLabel(
        t.stepProgress,
        String(currentStep).padStart(2, "0"),
        String(TOTAL_STEPS).padStart(2, "0"),
      ),
    [currentStep, t.stepProgress],
  );

  return (
    <div className="auth-form-shell reset-password-flow w-100" data-auth-portal={type}>
      <Stepper
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        label={stepProgressLabel}
      />

      {currentStep === 1 && (
        <ResetPasswordStep1
          key={step1Key}
          initialEmail={prefilledEmail}
          emailLocked={emailLocked}
          authPortal={type}
        />
      )}
      {currentStep === 2 && (
        <ResetPasswordStep2 email={prefilledEmail} authPortal={type} />
      )}
    </div>
  );
};

ResetPasswordFlow.propTypes = {
  type: PropTypes.oneOf(Object.values(AUTH_CONTEXT_TYPE)),
};

export default ResetPasswordFlow;
